import { useMemo, useRef, useState } from "react";
import { Volume2 } from "lucide-react";
import { fetchAcharyaAudio } from "@/lib/acharya/acharya-service";
import { publicMantraUrl, resolveMantraId } from "@/lib/audio/mantra-registry";

function resolveSrc(payload) {
  if (!payload) return null;
  if (payload.url) return payload.url;
  if (payload.audio_base64 && payload.mime_type) {
    return `data:${payload.mime_type};base64,${payload.audio_base64}`;
  }
  return null;
}

/**
 * Minimal speaker icon to play a mantra (16–20px).
 * - mantra: registry id or Sanskrit text
 * - Plays local /audio/mantras/*.wav when available, else fetchAcharyaAudio(mode="mantra")
 */
export function MantraAudioIcon({
  mantra = "om",
  mantraId,
  token,
  title = "Play mantra",
  size = 18,
  className = "",
}) {
  const [loading, setLoading] = useState(false);
  const [src, setSrc] = useState(null);
  const audioRef = useRef(null);

  const resolvedId = useMemo(
    () => mantraId ?? resolveMantraId(mantra) ?? (typeof mantra === "string" ? mantra : "om"),
    [mantra, mantraId]
  );

  const localUrl = useMemo(() => publicMantraUrl(resolveMantraId(mantra) ?? resolvedId), [mantra, resolvedId]);

  const aria = useMemo(() => (loading ? "Loading mantra audio" : title), [loading, title]);

  const play = async () => {
    if (loading) return;
    try {
      setLoading(true);
      if (!src) {
        if (localUrl) {
          setSrc(localUrl);
          queueMicrotask(() => audioRef.current?.play?.().catch(() => {}));
          return;
        }
        const payload = await fetchAcharyaAudio(resolvedId, "mantra", token);
        const s = resolveSrc(payload);
        if (s) setSrc(s);
        queueMicrotask(() => audioRef.current?.play?.().catch(() => {}));
      } else {
        await audioRef.current?.play?.().catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className={`inline-flex items-center ${className}`} data-testid="mantra-audio-icon">
      <button
        type="button"
        onClick={play}
        title={title}
        aria-label={aria}
        className="inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground transition-colors"
        style={{ width: size + 6, height: size + 6 }}
      >
        <Volume2 width={size} height={size} />
      </button>
      <audio ref={audioRef} src={src ?? undefined} preload="auto" />
    </span>
  );
}
